import React from "react";
import { RefreshCw } from "lucide-react";
import type { DuplicateInfo, DuplicateChoice } from "./types";

interface Props {
  duplicates: DuplicateInfo[];
  duplicateChoices: Record<string, DuplicateChoice>;
  onChoiceChange: (existingId: string, choice: DuplicateChoice) => void;
  onSetAll: (choice: DuplicateChoice) => void;
  submitting: boolean;
  onApply: () => void;
}

const StepDuplicateResolution: React.FC<Props> = ({
  duplicates,
  duplicateChoices,
  onChoiceChange,
  onSetAll,
  submitting,
  onApply,
}) => (
  <div>
    <div className="flex items-start gap-2 mb-1">
      <RefreshCw size={18} className="text-amber-500 flex-shrink-0 mt-0.5" />
      <div>
        <h3 className="font-semibold">Resolve Duplicate Products</h3>
        <p className="text-xs text-gray-500 mt-0.5">
          {duplicates.length} product{duplicates.length !== 1 ? "s" : ""} in your file already
          exist in this store. Choose what to do with each one. All other products have already been
          imported.
        </p>
      </div>
    </div>

    {/* Bulk select actions */}
    <div className="flex items-center gap-3 mt-3 mb-4 text-xs">
      <span className="text-gray-500 font-medium">Select all:</span>
      <button
        onClick={() => onSetAll("update")}
        className="px-2.5 py-1 rounded border border-blue-300 text-blue-700 hover:bg-blue-50"
      >
        Update all
      </button>
      <button
        onClick={() => onSetAll("skip")}
        className="px-2.5 py-1 rounded border border-gray-300 text-gray-600 hover:bg-gray-50"
      >
        Skip all
      </button>
    </div>

    {/* Duplicate cards */}
    <div className="space-y-3 max-h-[380px] overflow-y-auto pr-1">
      {duplicates.map((dup) => {
        const choice = duplicateChoices[dup.existingId] ?? "update";
        const fields: Array<{
          key: keyof DuplicateInfo["existingProduct"];
          label: string;
          fmt?: (v: number | string | null) => string;
        }> = [
          { key: "category", label: "Category" },
          { key: "cost_price", label: "Cost Price", fmt: (v) => (v !== null ? String(v) : "—") },
          { key: "selling_price", label: "Selling Price", fmt: (v) => (v !== null ? String(v) : "—") },
          { key: "quantity", label: "Quantity", fmt: (v) => (v !== null ? String(v) : "—") },
        ];

        return (
          <div
            key={dup.existingId}
            className={`border rounded-md p-3 transition-colors ${
              choice === "update"
                ? "border-blue-300 bg-blue-50/40"
                : "border-gray-200 bg-gray-50/60 opacity-60"
            }`}
          >
            {/* Card header */}
            <div className="flex justify-between items-center mb-2">
              <span className="font-medium text-sm">{dup.incoming.name}</span>
              <span className="text-xs text-gray-400">Row {dup.row}</span>
            </div>

            {/* Comparison table */}
            <table className="w-full text-xs mb-3 border-collapse">
              <thead>
                <tr className="text-gray-500">
                  <th className="text-left py-1 pr-3 font-medium w-1/3">Field</th>
                  <th className="text-left py-1 pr-3 font-medium w-1/3">Existing</th>
                  <th className="text-left py-1 font-medium w-1/3">Incoming (CSV)</th>
                </tr>
              </thead>
              <tbody>
                {fields.map(({ key, label, fmt }) => {
                  const existingVal = dup.existingProduct[key];
                  const incomingVal = dup.incoming[key as keyof DuplicateInfo["incoming"]];
                  const existingDisplay = fmt ? fmt(existingVal as number | string | null) : (existingVal ?? "—");
                  const incomingDisplay = incomingVal !== undefined ? String(incomingVal) : "—";
                  const changed = String(existingDisplay) !== incomingDisplay;
                  return (
                    <tr key={key} className="border-t border-gray-200">
                      <td className="py-1 pr-3 text-gray-500">{label}</td>
                      <td className="py-1 pr-3 font-mono text-gray-700">{String(existingDisplay)}</td>
                      <td className={`py-1 font-mono ${changed ? "text-blue-700 font-semibold" : "text-gray-700"}`}>
                        {incomingDisplay}
                        {changed && <span className="ml-1 text-blue-400 font-normal">↑</span>}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>

            {/* Update / Skip toggle */}
            <div className="flex gap-4 text-xs">
              <label className="flex items-center gap-1.5 cursor-pointer">
                <input
                  type="radio"
                  name={`dup-${dup.existingId}`}
                  checked={choice === "update"}
                  onChange={() => onChoiceChange(dup.existingId, "update")}
                  className="accent-blue-600"
                />
                <span className={choice === "update" ? "text-blue-700 font-medium" : "text-gray-500"}>
                  Update existing
                </span>
              </label>
              <label className="flex items-center gap-1.5 cursor-pointer">
                <input
                  type="radio"
                  name={`dup-${dup.existingId}`}
                  checked={choice === "skip"}
                  onChange={() => onChoiceChange(dup.existingId, "skip")}
                  className="accent-gray-400"
                />
                <span className={choice === "skip" ? "text-gray-700 font-medium" : "text-gray-500"}>
                  Skip
                </span>
              </label>
            </div>
          </div>
        );
      })}
    </div>

    <button
      onClick={onApply}
      disabled={submitting}
      className="mt-4 w-full bg-blue-600 text-white p-3 rounded-md disabled:opacity-50"
    >
      {submitting ? "Applying…" : "Apply Choices"}
    </button>
  </div>
);

export default StepDuplicateResolution;
